import { useEffect, useState, useCallback } from "react";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import Pagination from "../../../../components/Pagination";
import api from "../../../../api/api";
import * as bootstrap from "bootstrap";
import BackButton from "../../../../components/BackButton";
import "../../Common/form.css";
import Table from "../../../../components/table";
import usePagination from "../../../../utils/usePagination";
import { useAuth } from "../../../../context/AuthContext";
import SearchIcon from "@mui/icons-material/Search";
import { Modal } from "react-bootstrap";

// Helper to check if port is prioritized (handles both boolean and number)
const isPrioritized = (port) => {
  return port.isPriority === 1 || port.isPriority === true;
};

const Ports = () => {
  const { user } = useAuth();
  const [ports, setPorts] = useState([]);
  const [search, setSearch] = useState("");
  const [showOnlyPrioritized, setShowOnlyPrioritized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);

  const [newPort, setNewPort] = useState({
    portName: "",
    countryName: "",
  });

  const [portErrors, setPortErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMsg, setFailMsg] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editPort, setEditPort] = useState({ portName: "", countryName: "" });
  const [editPortId, setEditPortId] = useState(null);
  const [editPortErrors, setEditPortErrors] = useState({});

  const parseCombinedPortName = (combined) => {
    const match = combined.match(/^(.*)\s\((.*)\)$/);
    if (match) {
      return { portName: match[1], countryName: match[2] };
    }
    return { portName: combined, countryName: "" };
  };

  const validatePort = () => {
    let errors = {};

    if (!newPort.portName || newPort.portName.trim() === "") {
      errors.portName = "Port name is required";
    }

    if (!newPort.countryName || newPort.countryName.trim() === "") {
      errors.countryName = "Country name is required";
    }

    setPortErrors(errors);

    return Object.keys(errors).length === 0;
  };
  const handlePortChange = (e) => {
    const { name, value } = e.target;
    const capitalized =
      value.charAt(0).toUpperCase() + value.slice(1);
    setNewPort({
      ...newPort,
      [name]: capitalized,
    });
  };

  const handleAddPort = async () => {
    try {
      const res = await api.post("/ports", {
        Port_names: newPort.portName,
        Country_name: newPort.countryName,
      });

      setPorts((prev) => [...prev, res.data]);

      setNewPort({
        portName: "",
        countryName: "",
      });

      setPortErrors({});
      setShowAddModal(false);
      setSuccessMsg("Port added successfully!");
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Error adding port:", err);
      setFailMsg(
        "Failed to add port: " + (err.response?.data?.error || err.message),
      );
      setShowFailModal(true);
    }
  };

  const handleEditClick = (port) => {
    const { portName, countryName } = parseCombinedPortName(port.Port_names);
    setEditPortId(port.Port_id);
    setEditPort({ portName, countryName });
    setEditPortErrors({});
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const capitalized =
      value.charAt(0).toUpperCase() + value.slice(1);
    setEditPort({
      ...editPort,
      [name]: capitalized,
    });
  };

  const validateEditPort = () => {
    let errors = {};

    if (!editPort.portName || editPort.portName.trim() === "") {
      errors.portName = "Port name is required";
    }

    if (!editPort.countryName || editPort.countryName.trim() === "") {
      errors.countryName = "Country name is required";
    }

    setEditPortErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const handleUpdatePort = async () => {
    try {
      const res = await api.put("/ports/" + editPortId, {
        Port_names: editPort.portName,
        Country_name: editPort.countryName,
      });

      setPorts((prev) =>
        prev.map((p) => (p.Port_id === editPortId ? res.data : p)),
      );

      setEditPort({ portName: "", countryName: "" });
      setEditPortErrors({});
      setShowEditModal(false);
      setSuccessMsg("Port updated successfully!");
      setShowSuccessModal(true);
    } catch (err) {
      console.error("Error updating port:", err);
      setFailMsg(
        "Failed to update port: " + (err.response?.data?.error || err.message),
      );
      setShowFailModal(true);
    }
  };

  //  Role check - backend uses user_role (with underscore)
  const userRole = user?.user_role?.toLowerCase() || "";
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  const isSuperAdmin = user?.user_role === "SuperAdmin";

  // Filter ports by name and priority status
  const filteredPorts = ports.filter(
    (port) =>
      port.Port_names?.toLowerCase().includes(search.toLowerCase()) &&
      (!showOnlyPrioritized || isPrioritized(port)),
  );

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredPorts, 10);

  // =========================
  // Fetch Ports
  // =========================
  useEffect(() => {
    const fetchPorts = async () => {
      setLoading(true);
      try {
        const res = await api.get("/ports");
        setPorts(res.data || []);
      } catch (err) {
        console.error("Error fetching ports:", err);
        setError(
          "Failed to fetch ports: " +
            (err.response?.data?.error || err.message),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPorts();
  }, []);

  // =========================
  // Tooltip Init
  // =========================
  // =========================
  // Tooltip Init & Cleanup
  // =========================
  useEffect(() => {
    // 1. Find all elements that have a tooltip
    const tooltipTriggerList = Array.from(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );

    // 2. Initialize new tooltips and store the instances
    const tooltipInstances = tooltipTriggerList.map((tooltipTriggerEl) => {
      return new bootstrap.Tooltip(tooltipTriggerEl, {
        trigger: "hover", // Ensure it only works on hover, not click/focus
      });
    });

    // 3. Cleanup function: This runs before the next effect or on unmount
    return () => {
      tooltipInstances.forEach((tooltip) => {
        tooltip.dispose();
      });
    };
  }, [currentItems, search, showOnlyPrioritized]);

  // =========================
  // Toggle Priority
  // =========================
  const handleStarClick = async (port) => {
    if (!isAdmin) return;
    const element = document.activeElement;
    if (element) {
      const tooltip = bootstrap.Tooltip.getInstance(element);
      if (tooltip) tooltip.hide();
    }

    const wasPrioritized = isPrioritized(port);

    // Optimistic update
    setPorts((prev) =>
      prev.map((p) =>
        p.Port_id === port.Port_id
          ? { ...p, isPriority: wasPrioritized ? false : true }
          : p,
      ),
    );

    try {
      const endpoint = wasPrioritized ? "remove-priority" : "set-priority";
      await api.put("/master-data/ports/" + port.Port_id + "/" + endpoint);
    } catch (err) {
      console.error("Error updating priority:", err);
      // Revert on error
      setPorts((prev) =>
        prev.map((p) =>
          p.Port_id === port.Port_id ? { ...p, isPriority: wasPrioritized } : p,
        ),
      );
      alert(
        "Failed to update priority: " +
          (err.response?.data?.error || err.message),
      );
    }
  };

  if (error) return <p className="text-danger">{error}</p>;

  // =========================
  // Table Columns
  // =========================
  const columns = [
    { label: "Port Name", accessor: "Port_names" },
    {
      label: "Priority Status",
      render: (row) => (
        <span
          className={`badge ${isPrioritized(row) ? "bg-primary" : "bg-secondary"}`}
        >
          {isPrioritized(row) ? "Prioritized" : "Normal"}
        </span>
      ),
    },
    {
      label: "Priority",
      render: (row) =>
        isPrioritized(row) ? (
          <StarIcon
            style={{
              cursor: isAdmin ? "pointer" : "default",
              color: "#1e305e",
            }}
            onClick={() => isAdmin && handleStarClick(row)}
            data-bs-toggle="tooltip"
            title={isAdmin ? "Remove priority" : "Priority Port"}
          />
        ) : (
          <StarBorderIcon
            style={{
              cursor: isAdmin ? "pointer" : "default",
              color: "#1e305e",
            }}
            onClick={() => isAdmin && handleStarClick(row)}
            data-bs-toggle="tooltip"
            title={isAdmin ? "Set as priority" : "Normal Port"}
          />
        ),
    },
    ...(isSuperAdmin
      ? [
          {
            label: "Actions",
            render: (row) => (
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => handleEditClick(row)}
                title="Edit Port"
              >
                <EditIcon style={{ fontSize: 18 }} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <BackButton />
      <div className="d-flex align-items-center gap-3 mb-3">
        {/* Search Input */}
        <div className="position-relative flex-grow-1">
          <input
            type="search"
            className="form-control ps-5"
            placeholder="Search by Ports..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <SearchIcon
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              color: "#6b7280",
              width: "16px",
              height: "16px",
            }}
          />
        </div>
       
        {/* Checkbox */}
        <div className="form-check mb-0">
          <input
            className="form-check-input"
            type="checkbox"
            id="showOnlyPrioritized"
            checked={showOnlyPrioritized}
            onChange={(e) => setShowOnlyPrioritized(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="showOnlyPrioritized">
            Show Only Prioritized Ports
          </label>
        </div>
         {isSuperAdmin && (
          <button
            className="btn btn-primary"
            onClick={() => setShowAddModal(true)}
          >
            + Add New Port
          </button>
        )}
      </div>
      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No Ports Found"
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />

      {/* Add Port Modal */}
      {showAddModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Port</h5>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddModal(false);
                    setPortErrors({});
                    setShowSuccessModal(false);
                    setShowFailModal(false);
                  }}
                />
              </div>

               <div className="modal-body">
                 <div className="mb-3">
                   <label className="form-label">Port Name</label>

                  <input
                    type="text"
                    className={`form-control ${
                      portErrors.portName ? "is-invalid" : ""
                    }`}
                    name="portName"
                    value={newPort.portName}
                    onChange={(e) => {
                      handlePortChange(e);
                      setPortErrors((prev) => ({
                        ...prev,
                        portName: "",
                      }));
                    }}
                    placeholder="Enter Port Name"
                  />

                  {portErrors.portName && (
                    <div className="invalid-feedback d-block">
                      {portErrors.portName}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Country Name</label>

                  <input
                    type="text"
                    className={`form-control ${
                      portErrors.countryName ? "is-invalid" : ""
                    }`}
                    name="countryName"
                    value={newPort.countryName}
                    onChange={(e) => {
                      handlePortChange(e);
                      setPortErrors((prev) => ({
                        ...prev,
                        countryName: "",
                      }));
                    }}
                    placeholder="Enter Country Name"
                  />

                  {portErrors.countryName && (
                    <div className="invalid-feedback d-block">
                      {portErrors.countryName}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setPortErrors({});
                    setShowSuccessModal(false);
                    setShowFailModal(false);
                  }}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (validatePort()) {
                      handleAddPort();
                    }
                  }}
                >
                  Save Port
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}

        {/* Edit Port Modal */}
        {showEditModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Port</h5>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditPortErrors({});
                    }}
                  />
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Port Name</label>

                    <input
                      type="text"
                      className={`form-control ${
                        editPortErrors.portName ? "is-invalid" : ""
                      }`}
                      name="portName"
                      value={editPort.portName}
                      onChange={(e) => {
                        handleEditChange(e);
                        setEditPortErrors((prev) => ({
                          ...prev,
                          portName: "",
                        }));
                      }}
placeholder="Enter Port Name"
                    />

                    {editPortErrors.portName && (
                      <div className="invalid-feedback d-block">
                        {editPortErrors.portName}
                      </div>
                    )}
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Country Name</label>

                    <input
                      type="text"
                      className={`form-control ${
                        editPortErrors.countryName ? "is-invalid" : ""
                      }`}
                      name="countryName"
                      value={editPort.countryName}
                      onChange={(e) => {
                        handleEditChange(e);
                        setEditPortErrors((prev) => ({
                          ...prev,
                          countryName: "",
                        }));
                      }}
placeholder="Enter Country Name"
                    />

                    {editPortErrors.countryName && (
                      <div className="invalid-feedback d-block">
                        {editPortErrors.countryName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEditModal(false);
                      setEditPortErrors({});
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (validateEditPort()) {
                        handleUpdatePort();
                      }
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        <Modal
          show={showSuccessModal}
          onHide={() => {
            setShowSuccessModal(false);
            setSuccessMsg("");
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Success</Modal.Title>
          </Modal.Header>
          <Modal.Body>{successMsg}</Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-success"
              onClick={() => setShowSuccessModal(false)}
            >
              OK
            </button>
          </Modal.Footer>
        </Modal>

        {/* Failure Modal */}
        <Modal
          show={showFailModal}
          onHide={() => setShowFailModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Error</Modal.Title>
          </Modal.Header>
          <Modal.Body>{failMsg}</Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-danger"
              onClick={() => setShowFailModal(false)}
            >
              OK
            </button>
          </Modal.Footer>
        </Modal>
      </>
    );
  };

export default Ports;